<template>
  <div id="app">
    <ComponentOfflineBanner :class="{ 'no-print': printMode }"></ComponentOfflineBanner>

    <router-view :class="['view', { 'no-print': printMode }]" />

    <ComponentToast :class="{ 'no-print': printMode }"></ComponentToast>

    <ComponentPrint></ComponentPrint>

    <PwaInstallPrompt></PwaInstallPrompt>
  </div>
</template>

<script>
import ComponentOfflineBanner from "@/components/ui/ComponentOfflineBanner";
import ComponentPrint from "@/components/ui/ComponentPrint";
import ComponentToast from "@/components/ui/ComponentToast";
import PwaInstallPrompt from "@/components/PwaInstallPrompt";
import lodash from "@/utils/lodash";

export default {
  name: "App",
  components: {
    ComponentOfflineBanner,
    ComponentPrint,
    ComponentToast,
    PwaInstallPrompt,
  }, //components
  data() {
    return {
      printMode: false,
      recentErrors: new Set(),
    };
  },
  computed: {}, //methods
  watch: {
    $route: function (value, oldValue) {
      this.defineEditor();
    },
  }, //computed
  created() {
    const vm = this;

    const noSessionRoutes = ["/login", "/signup", "/subscription-suspended"];

    // Store event handler for cleanup
    this.handleToastClose = () => {
      this.printMode = false;
    };
    this.$eventbus.on("ComponentToast/close", this.handleToastClose);

    document.addEventListener(
      "visibilitychange",
      (e) => {
        if (noSessionRoutes.indexOf(vm.$route.path) < 0 && !(vm.$route.meta && vm.$route.meta.public)) {
          if (document.visibilityState === "visible") {
            vm.$helpers.checkFrontendSessionExpiry({
              route: vm.$route,
              router: vm.$router,
            });
          }
        }
      },
      false,
    );

    document.addEventListener(
      "click",
      (e) => {
        if (noSessionRoutes.indexOf(vm.$route.path) < 0 && !(vm.$route.meta && vm.$route.meta.public)) {
          if (document.visibilityState === "visible") {
            vm.$helpers.checkFrontendSessionExpiry({
              e,
              route: vm.$route,
              router: vm.$router,
            });
          }
        }
      },
      false,
    );

    window.addEventListener(
      "httpRequestError",
      (e) => {
        try {
          const error = e.detail.error;
          const status = error.response?.status;
          const url = error.config?.url || "unknown";

          // Create error key for deduplication (status + url)
          const errorKey = `${status}-${url}`;

          // Skip if we've shown this error recently (within 3 seconds)
          if (this.recentErrors.has(errorKey)) {
            return;
          }

          // Add to recent errors and remove after 3 seconds
          this.recentErrors.add(errorKey);
          setTimeout(() => {
            this.recentErrors.delete(errorKey);
          }, 3000);

          // Determine user-friendly error message based on status code
          let errorMessage = "Não conseguimos concluir agora. Tente de novo em instantes.";
          let shouldLogout = false;

          if (status === 401) {
            if (noSessionRoutes.includes(this.$route.path)) return;
            errorMessage = error.response?.data?.idle
              ? "Sua sessão foi encerrada por inatividade. Entre novamente."
              : "Sessão expirada. Faça login novamente";
            shouldLogout = true;
          } else if (status === 403 && error.response?.data?.password_change_required) {
            try {
              const user = JSON.parse(localStorage.getItem("ordexa/user") || "{}");
              user.password_change_due_at = user.password_change_due_at || new Date().toISOString();
              localStorage.setItem("ordexa/user", JSON.stringify(user));
            } catch (err) {
              // ignore
            }
            if (this.$route.path !== "/personal-profile/password") {
              this.$router.push({ path: "/personal-profile/password", query: { obrigatorio: "1" } });
            }
            return;
          } else if (status === 403) {
            errorMessage = "Você não tem permissão para fazer isso. Se precisar, peça ao responsável pela conta.";
          } else if (status === 404) {
            errorMessage = "Não encontramos o que você procurou. Ele pode ter sido removido.";
          } else if (status === 422) {
            // The backend usually says exactly what is wrong (error / errors[0]): show that.
            const data = error.response?.data || {};
            const first = Array.isArray(data.errors) ? data.errors[0] : null;
            const detail = typeof data.error === "string" ? data.error : typeof first === "string" ? first : "";
            errorMessage = detail || "Algo nas informações enviadas precisa de ajuste. Confira e tente de novo.";
          } else if (status === 500) {
            errorMessage = "Não conseguimos carregar agora. Tente de novo em instantes.";
          } else if (status >= 500) {
            errorMessage = "O Ordexa está fora do ar por um momento. Tente de novo em instantes.";
          } else if (!status || error.message === "Network Error") {
            errorMessage = "Sem conexão com a internet. Confira a rede e tente de novo.";
          }

          this.$eventbus.emit("ComponentToast/add", {
            autoHide: true,
            autoHideDelay: shouldLogout ? 8000 : 5000,
            content: errorMessage,
            icon: null,
            showCloseButton: false,
            type: "error",
          });

          if (shouldLogout) {
            setTimeout(() => {
              this.$store.dispatch("App/logout");
              this.$router.replace(
                { name: "Login" },
                () => {},
                () => {},
              );
            }, 1000);
          }
        } catch (err) {
          // Fallback error handling if response structure is unexpected
          console.error("Error handler failed:", err);
        }
      },
      false,
    );

    window.addEventListener("offline", (e) => {
      this.$eventbus.emit("ComponentToast/add", {
        autoHide: true,
        content: "Você está offline. Verifique sua conexão.",
        icon: null,
        showCloseButton: false,
        type: "error",
      });
    });

    this.defineEditor();
  }, //created
  unmounted() {
    // Clean up event bus listeners
    if (this.handleToastClose) {
      this.$eventbus.off("ComponentToast/close", this.handleToastClose);
    }
  }, //unmounted
  methods: {
    defineEditor() {
      const route = this.$route;

      if (!lodash.isEmpty(route.query)) {
        if (route.query.editor) {
          sessionStorage.setItem("ordexa/editor", route.query.editor);

          window.location.href = "#" + this.$route.path;

          setTimeout(() => {
            window.location.reload();
          }, 100);
        }
      }
    },

    //Events
  }, //watch
};
</script>

<style lang="scss">
/* Design tokens: one palette, radius and shadow scale for the whole app */
:root {
  --brand: #29b1cc;
  --brand-dark: #1a8ea8;
  --brand-soft: #e8f7fa;
  /* Brand color for small text on white (4.5:1+); --brand itself is for fills */
  --brand-text: #127a8f;
  --text: #1f2d33;
  --text-muted: #5f6f75;
  --border: #dde5e8;
  --bg: #f4f6f7;
  --surface: #fff;
  --danger: #d64545;
  --success: #2e9e57;
  --warning: #c07800;
  --radius: 10px;
  --radius-sm: 8px;
  --shadow-sm: 0 1px 3px rgba(16, 42, 51, 0.08);
  --shadow: 0 2px 10px rgba(16, 42, 51, 0.08);
}

* {
  font-family: "Montserrat", sans-serif;
}

body {
  color: var(--text);
  /* Breathing room: without this the browser uses "normal" (~1.2), which makes paragraphs look glued together */
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

h1,
h2,
h3,
h4 {
  color: var(--text);
  letter-spacing: -0.01em;
  line-height: 1.3;
}

p {
  line-height: 1.55;
}

p + p {
  margin-top: 0.75em;
}

label {
  line-height: 1.4;
}

small {
  line-height: 1.45;
}

li {
  line-height: 1.5;
}

/* Form controls do not inherit line-height: keep text inside them comfortable */
button,
select {
  line-height: 1.35;
}

input,
textarea {
  line-height: 1.4;
}

::placeholder {
  color: #8a989d;
  opacity: 1;
}

h1 {
  font-weight: 600;
}

h2,
h3 {
  font-weight: 600;
}

strong,
b {
  font-weight: 600;
}

html {
  scroll-behavior: smooth;
}

button {
  cursor: pointer;
  font-size: 1em;
  outline: none;
  transition: all 0.2s ease;
}

button:hover:not(:disabled) {
  filter: brightness(1.04);
}

button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

input,
textarea {
  font-size: 1em;
  font-weight: 400;
  color: var(--text);
  outline: none;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

input:focus,
textarea:focus {
  border-color: #29b1cc;
  box-shadow: 0 0 0 4px rgba(41, 177, 204, 0.15);
}

/* Enhanced focus-visible for accessibility */
*:focus-visible {
  outline: 3px solid #29b1cc;
  outline-offset: 2px;
  border-radius: 4px;
}

button:focus-visible {
  outline: 3px solid #29b1cc;
  outline-offset: 3px;
}

input:focus-visible,
textarea:focus-visible,
select:focus-visible {
  outline: none;
  border-color: #29b1cc;
  box-shadow: 0 0 0 4px rgba(41, 177, 204, 0.2);
}

a:focus-visible {
  outline: 2px solid #29b1cc;
  outline-offset: 2px;
  border-radius: 2px;
}

fieldset {
  background-color: transparent;
  border: none;
  margin: 0;
  padding: 0;
}

hr {
  border: none;
  border-bottom: 1px solid #d0d0d0;
  margin: 1em 0;
}

a {
  color: #29b1cc;
  transition: color 0.2s ease;
}

a:hover {
  color: #1a8a9f;
}

#app {
  display: flex;
  align-items: stretch;

  font-weight: 400;
  width: 100%;
}

.view {
  width: 100%;
}

.btn-default {
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);

  border: none;
  border-radius: var(--radius-sm);
  color: #fff;
  font-weight: 600;
  padding: 0.7em 1.3em;

  transition: all 0.2s ease;
  box-shadow: var(--shadow-sm);
}

.btn-default:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: var(--shadow);
}

.btn-default:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.btn-default img {
  height: 1em;
  margin-right: 0.5em;
  vertical-align: middle;
}

.btn-primary {
  background-color: var(--brand);
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--brand-dark);
}

.btn-secondary {
  background-color: var(--surface);
  color: var(--brand-dark);
  border: 1px solid #b9dde5;
  box-shadow: none;
}

.btn-secondary:hover:not(:disabled) {
  background-color: var(--brand-soft);
}

.btn-secondary img {
  filter: invert(45%) sepia(60%) saturate(500%) hue-rotate(150deg);
}

.btn-terciary {
  background-color: transparent;
  color: #333;
  box-shadow: none;
}

.btn-terciary:hover:not(:disabled) {
  background-color: #f5f5f5;
  box-shadow: none;
}

.btn-primary-red {
  background-color: #eb5757;
}

.btn-primary-red:hover:not(:disabled) {
  background-color: #d63e3e;
}

.btn-primary-green {
  background-color: #2aaa64;
}

.btn-primary-green:hover:not(:disabled) {
  background-color: #228c52;
}

.btn-primary-outline {
  background-color: transparent;
  border: 2px solid #29b1cc;
  color: #29b1cc;
  box-shadow: none;
}

.btn-primary-outline:hover:not(:disabled) {
  background-color: #29b1cc;
  color: #fff;
}

.container-group-input {
  text-align: left;
}

.container-group-input label {
  display: block;
  margin-bottom: 0.45em;
}

.required-mark {
  color: #d00;
  margin-left: 0.35em;
}

.container-view-header {
  display: flex;
  align-items: center;
}

.container-view-header h1 {
  font-size: 1.6em;
  font-weight: 600;
  line-height: 110%;
  margin: 0;
}

.container-view-header > div {
  flex: 1;
}

.container-view-header .col-left {
  margin-right: 1em;
}

.container-view-header .col-right {
  text-align: right;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.6em;
}

.fade-enter-active,
.fade-leave-active {
  transition-duration: 0.2s;
  transition-property: opacity;
  transition-timing-function: ease;
}

.fade-enter,
.fade-leave-active {
  opacity: 0;
}

.icon-circle-segment-animated {
  height: 1.2em;
  margin-right: 0.5em;
  vertical-align: middle;

  animation-duration: 1s;
  animation-iteration-count: infinite;
  animation-name: iconCircleSegmentAnimated;
}

.before-content-small:before {
  content: "Pequena" !important;
}

.before-content-large:before {
  content: "Grande" !important;
}

.before-content-huge:before {
  content: "Enorme" !important;
}

@keyframes iconCircleSegmentAnimated {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(359deg);
  }
}

@media (max-width: 767px) {
  /* Touch-friendly button sizes for mobile */
  .btn-default {
    padding: 1em 1.75em;
    font-size: 1.05em;
    min-height: 44px; /* Apple's recommended minimum touch target */
  }

  /* Touch targets: real buttons get 44px; inline links keep their text size */
  button:not(.btn-default):not(td button):not(th button) {
    min-height: 40px;
  }

  /* Larger tap targets for text fields (not checkboxes/radios) */
  input:not([type="checkbox"]):not([type="radio"]):not([type="range"]),
  textarea,
  select {
    font-size: 16px; /* Prevents zoom on iOS */
    min-height: 44px;
  }

  input[type="checkbox"],
  input[type="radio"] {
    width: 20px;
    height: 20px;
  }

  /* Short single-line fields (CPF, phone, date) should not look half-broken next to full-width ones */
  textarea {
    line-height: 1.5;
    padding-top: 0.6em;
    padding-bottom: 0.6em;
  }

}


@media (min-width: 1200px) {
}

@media print {
  .no-print {
    display: none !important;
  }
}

/* Editor toolbar (TinyMCE) buttons must never get generic touch sizes */
.tox .tox-tbtn,
.tox .tox-split-button {
  min-width: 0 !important;
  min-height: 0 !important;
}

/* FullCalendar buttons in the brand style (Agenda and Calendário) */
.fc .fc-button-primary {
  background: var(--brand);
  border-color: var(--brand);
  border-radius: var(--radius-sm);
  font-weight: 600;
  text-transform: none;
  box-shadow: none;
}

.fc .fc-button-primary:not(:disabled):hover,
.fc .fc-button-primary:not(:disabled).fc-button-active,
.fc .fc-button-primary:not(:disabled):active {
  background: var(--brand-dark);
  border-color: var(--brand-dark);
}

.fc .fc-button-primary:disabled {
  background: var(--brand);
  border-color: var(--brand);
  opacity: 0.45;
}

.fc .fc-toolbar-title {
  font-size: 1.15em;
}

@media (max-width: 767px) {
  .fc .fc-toolbar {
    flex-wrap: nowrap;
    gap: 6px;
  }

  .fc .fc-toolbar-title {
    font-size: 1em;
    min-width: 0;
    text-align: center;
  }

  .fc .fc-toolbar-chunk {
    flex: none;
  }

  .fc .fc-toolbar-chunk:nth-child(2) {
    flex: 1 1 auto;
    min-width: 0;
  }

  .fc .fc-button {
    min-height: 40px;
    padding: 0.3em 0.7em;
  }

  .fc .fc-footer-toolbar .fc-button {
    min-height: 44px;
  }

  .fc .fc-col-header-cell-cushion {
    font-size: 0.72em;
    padding: 4px 0;
  }
}

.tox-notification--warning {
  display: none !important;
}
</style>
